import { WS_STATUS } from 'constants/wsStatus';

class WebSocketManager {
  constructor({
    baseUrl,
    tenantId,
    userId,
    accessLevel,
    maxReconnectAttempts = 5,
    reconnectInterval = 3000,
    pingInterval = 15000,
    pongTimeout = 5000,
    maxMissedHeartbeats = 2,
    longRetryInterval = 30000, // Retry every 30 seconds after max attempts
    messageTTL = 5 * 60 * 1000, // 5 minutes before a queued message is considered stale
    maxQueueSize = 50,
    onSendError = null, // (payload, error) => void — called when all retries are exhausted
  }) {
    this.baseUrl = baseUrl;
    this.tenantId = tenantId;
    this.userId = userId;
    this.accessLevel = accessLevel;

    this.maxReconnectAttempts = maxReconnectAttempts;
    this.reconnectInterval = reconnectInterval;
    this.longRetryInterval = longRetryInterval;

    this.socket = null;
    this.status = WS_STATUS.DISCONNECTED;
    this.listeners = {};
    this.reconnectAttempts = 0;
    this.reconnectTimeout = null;

    // Heartbeat configuration
    this.pingInterval = pingInterval;
    this.pongTimeout = pongTimeout;
    this.maxMissedHeartbeats = maxMissedHeartbeats;
    this.heartbeatInterval = null;
    this.heartbeatTimeout = null;
    this.missedHeartbeats = 0;
    this.visibilityTimeout = null;

    // Outbound message queue
    this.messageQueue = [];
    this.messageTTL = messageTTL;
    this.maxQueueSize = maxQueueSize;
    this.onSendError = onSendError;
    this.pendingRetries = [];
    this._isFlushing = false;
  }

  // --------------------------
  // Public API
  // --------------------------

  connect() {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      console.log('🔄 WebSocket already connected — skipping reconnect', this.socket.readyState);
      return;
    }

    // Detach handlers from any lingering CLOSING/CLOSED socket to prevent
    // ghost onclose callbacks from firing handleReconnect after we move on.
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onclose = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket = null;
    }

    const url = `${this.baseUrl}?tenant_id=${encodeURIComponent(this.tenantId)}&user_id=${encodeURIComponent(this.userId)}${
      this.accessLevel ? `&access_level=${encodeURIComponent(this.accessLevel)}` : ''
    }`;

    this.setStatus(this.reconnectAttempts > 0 ? WS_STATUS.RECONNECTING : WS_STATUS.CONNECTING);

    this.socket = new WebSocket(url);

    this.attachEvents();
  }

  disconnect() {
    this.messageQueue = [];
    this.cleanup();
    this.setStatus(WS_STATUS.DISCONNECTED);
  }

  reconnect() {
    console.log('🔄 Manual reconnect triggered');

    // Clear any pending reconnection timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Reset counters
    this.reconnectAttempts = 0;
    this.missedHeartbeats = 0;

    // Close existing connection if any
    if (this.socket) {
      this.cleanup();
    }

    // Attempt new connection
    this.connect();
  }

  send(payload, retryCount = 0) {
    if (this.status === WS_STATUS.OFFLINE || this.status === WS_STATUS.DISCONNECTED) {
      const error = new Error(`Message send blocked: connection status is ${this.status}`);
      console.warn('⚠️', error.message);
      this._queueMessage(payload);
      this.onSendError?.(payload, error);
      return;
    }

    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      if (retryCount < 2) {
        const timeoutId = setTimeout(() => {
          this.send(payload, retryCount + 1);
        }, 1000);
        this.pendingRetries.push(timeoutId);
      } else {
        const error = new Error('Message send failed: socket not open after 2 retries');
        console.error('❌', error.message);
        this._queueMessage(payload);
        this.onSendError?.(payload, error);
      }
      return;
    }

    try {
      this.socket.send(JSON.stringify(payload));
    } catch (error) {
      if (retryCount < 2) {
        const timeoutId = setTimeout(() => {
          this.send(payload, retryCount + 1);
        }, 1000);
        this.pendingRetries.push(timeoutId);
      } else {
        console.error('❌ Send failed:', error);
        this._queueMessage(payload);
        this.onSendError?.(payload, error);
      }
    }
  }

  on(notificationType, callback) {
    if (!this.listeners[notificationType]) {
      this.listeners[notificationType] = [];
    }

    this.listeners[notificationType].push(callback);

    // Return unsubscribe function
    return () => {
      this.listeners[notificationType] = this.listeners[notificationType].filter((cb) => cb !== callback);
    };
  }

  off(notificationType, callback) {
    if (!this.listeners[notificationType]) return;

    this.listeners[notificationType] = this.listeners[notificationType].filter((cb) => cb !== callback);
  }

  getStatus() {
    return this.status;
  }

  // --------------------------
  // Internal Logic
  // --------------------------

  /**
   * Pushes a payload onto the outbound queue.
   * Evicts the oldest entry when maxQueueSize is reached.
   * Skipped during a flush to prevent re-queuing mid-flush failures.
   * @param {object} payload
   */
  _queueMessage(payload) {
    if (this._isFlushing) {
      console.warn('⚠️ Send failed during queue flush — message discarded to prevent re-queue loop:', payload);
      return;
    }
    if (this.messageQueue.length >= this.maxQueueSize) {
      const dropped = this.messageQueue.shift();
      console.warn('⚠️ Message queue full — dropped oldest message:', dropped.payload);
    }
    this.messageQueue.push({ payload, queuedAt: Date.now() });
    console.log(`📥 Message queued (queue size: ${this.messageQueue.length})`);
  }

  /**
   * Flushes all queued messages that have not yet exceeded messageTTL.
   * Stale messages are discarded. Called automatically on reconnect.
   * The _isFlushing guard prevents re-queuing if send() fails mid-flush.
   */
  flushQueue() {
    if (!this.messageQueue.length || this._isFlushing) return;

    const now = Date.now();
    const valid = this.messageQueue.filter(({ queuedAt }) => now - queuedAt < this.messageTTL);
    const expiredCount = this.messageQueue.length - valid.length;

    this.messageQueue = [];

    if (expiredCount > 0) {
      console.warn(`⚠️ Discarded ${expiredCount} expired queued message(s) (TTL: ${this.messageTTL}ms)`);
    }

    if (valid.length > 0) {
      console.log(`📤 Flushing ${valid.length} queued message(s)`);
      this._isFlushing = true;
      valid.forEach(({ payload }) => this.send(payload));
      this._isFlushing = false;
    }
  }

  attachEvents() {
    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.missedHeartbeats = 0;
      this.setStatus(WS_STATUS.CONNECTED);
      console.log('✅ WebSocket connected');

      // Start heartbeat
      this.startHeartbeat();

      // Replay any messages queued while disconnected
      this.flushQueue();
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Handle heartbeat response
        if (data.notification_type === 'heartbeat' && data.body === 'alive') {
          this.handleHeartbeatResponse();
          return; // Don't emit heartbeat to listeners
        }

        const type = data.notification_type;

        if (type && this.listeners[type]) {
          this.listeners[type].forEach((cb) => cb(data));
        }

        if (this.listeners['ALL']) {
          this.listeners['ALL'].forEach((cb) => cb(data));
        }
      } catch (err) {
        console.error('Invalid WS message:', err);
      }
    };

    this.socket.onerror = (err) => {
      console.error('WebSocket error:', err);
    };

    this.socket.onclose = (event) => {
      console.log(`WebSocket closed: code=${event.code}, reason=${event.reason || 'No reason provided'}`);

      // Stop heartbeat
      this.stopHeartbeat();

      // Only reconnect for abnormal closures (not 1000)
      if (event.code !== 1000) {
        this.handleReconnect();
      } else {
        this.setStatus(WS_STATUS.DISCONNECTED);
      }
    };

    // Remove duplicate listeners before adding
    window.removeEventListener('offline', this.handleOffline);
    window.removeEventListener('online', this.handleOnline);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    window.addEventListener('offline', this.handleOffline);
    window.addEventListener('online', this.handleOnline);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleReconnect() {
    // Clear any previously scheduled reconnect to prevent orphaned timeouts
    // accumulating when handleReconnect is called more than once in quick succession.
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.reconnectAttempts += 1;

    let backoff;
    let statusMessage;

    if (this.reconnectAttempts <= this.maxReconnectAttempts) {
      // Normal reconnection attempts with exponential backoff
      backoff = Math.min(this.reconnectInterval * Math.pow(1.5, this.reconnectAttempts - 1), 30000);
      statusMessage = `🔄 Reconnecting in ${backoff}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`;

      // Only set to RECONNECTING if not already OFFLINE (preserve OFFLINE status)
      if (this.status !== WS_STATUS.OFFLINE) {
        this.setStatus(WS_STATUS.RECONNECTING);
      }
    } else {
      // After max attempts, keep showing OFFLINE (not DISCONNECTED)
      backoff = this.longRetryInterval;
      statusMessage = `🔄 Long retry mode: attempting reconnect in ${backoff}ms...`;
      // Keep status as OFFLINE - don't change to DISCONNECTED
      if (this.status !== WS_STATUS.OFFLINE) {
        this.setStatus(WS_STATUS.OFFLINE);
      }
    }

    console.log(statusMessage);

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, backoff);
  }

  handleOffline = () => {
    console.warn('🌐 Network interface offline detected');
    this.stopHeartbeat();
    this.setStatus(WS_STATUS.OFFLINE);

    // Force-close the socket so readyState leaves OPEN.
    // Without this, connect() sees an OPEN socket and skips reconnection
    // when the network comes back. Code 4001 triggers onclose → handleReconnect.
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.close(4001, 'Network offline');
    }
  };

  handleOnline = () => {
    console.log('🌐 Network interface online detected — status:', this.status);

    // Only reconnect if we're actually disconnected/offline
    if ([WS_STATUS.DISCONNECTED, WS_STATUS.OFFLINE].includes(this.status)) {
      // Clear any pending backoff timeout so we reconnect immediately
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }

      this.reconnectAttempts = 0;
      this.missedHeartbeats = 0;
      this.connect();
    }
  };

  handleVisibilityChange = () => {
    // When tab becomes visible, check if we need to reconnect
    if (document.visibilityState === 'visible') {
      console.log('👁️ Tab became visible');

      // Clear any existing visibility check timeout (debouncing)
      if (this.visibilityTimeout) {
        clearTimeout(this.visibilityTimeout);
        this.visibilityTimeout = null;
      }

      // Debounce: Wait 1 second before checking (prevents rapid tab switching issues)
      this.visibilityTimeout = setTimeout(() => {
        // If disconnected/offline and no active reconnection attempt, try now
        if ([WS_STATUS.DISCONNECTED, WS_STATUS.OFFLINE].includes(this.status)) {
          // Clear any pending long retry timeout
          if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
          }

          console.log('🔄 Attempting immediate reconnect on tab visibility');
          this.reconnectAttempts = 0;
          this.missedHeartbeats = 0;
          this.connect();
        }
        // If connected, only send heartbeat if no timeout is already pending
        else if (this.status === WS_STATUS.CONNECTED && this.socket?.readyState === WebSocket.OPEN) {
          if (!this.heartbeatTimeout) {
            console.log('💓 Sending immediate heartbeat check on tab visibility');
            this.sendHeartbeat();
          } else {
            console.log('💓 Tab visible - heartbeat already pending, skipping');
            // Reset missed heartbeats on tab return (connection appears healthy)
            this.missedHeartbeats = 0;
          }
        }
      }, 1000);
    }
  };

  setStatus(status) {
    this.status = status;
    if (this.listeners['STATUS_CHANGE']) {
      this.listeners['STATUS_CHANGE'].forEach((cb) => cb(status));
    }
  }

  cleanup() {
    // Stop heartbeat
    this.stopHeartbeat();

    // Clear pending retry timeouts
    this.pendingRetries.forEach((timeoutId) => clearTimeout(timeoutId));
    this.pendingRetries = [];

    // Clear visibility timeout
    if (this.visibilityTimeout) {
      clearTimeout(this.visibilityTimeout);
      this.visibilityTimeout = null;
    }

    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onclose = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.close();
      this.socket = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    window.removeEventListener('offline', this.handleOffline);
    window.removeEventListener('online', this.handleOnline);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  // --------------------------
  // Heartbeat Logic
  // --------------------------

  startHeartbeat() {
    this.stopHeartbeat(); // Clear any existing intervals

    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.sendHeartbeat();
      }
    }, this.pingInterval);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  sendHeartbeat() {
    console.log('💓 Sending heartbeat...');

    // Clear any existing heartbeat timeout to prevent race conditions
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }

    const heartbeatMessage = {
      type: 'heartbeat',
      action: 'message',
      data: {
        tenant_id: this.tenantId,
      },
    };

    try {
      this.socket.send(JSON.stringify(heartbeatMessage));

      // Set timeout to wait for response
      this.heartbeatTimeout = setTimeout(() => {
        this.handleMissedHeartbeat();
      }, this.pongTimeout);
    } catch (error) {
      console.error('❌ Failed to send heartbeat:', error);
      this.handleMissedHeartbeat();
    }
  }

  handleHeartbeatResponse() {
    console.log('💓 Heartbeat alive received');

    // Clear the timeout
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }

    // Reset missed heartbeats counter
    this.missedHeartbeats = 0;
  }

  handleMissedHeartbeat() {
    this.missedHeartbeats++;
    console.warn(`⚠️ Missed heartbeat #${this.missedHeartbeats}/${this.maxMissedHeartbeats}`);

    if (this.missedHeartbeats >= this.maxMissedHeartbeats) {
      console.error('❌ Connection is dead (no heartbeat response). Forcing reconnect...');

      // Mark as offline
      this.setStatus(WS_STATUS.OFFLINE);
      this.stopHeartbeat();

      // Close the zombie connection with abnormal closure code
      // Use 1006 (abnormal closure) instead of 1000 to trigger reconnection
      if (this.socket) {
        this.socket.close(1006, 'Heartbeat timeout');
      }

      // This will trigger onclose → handleReconnect
    }
  }
}

export default WebSocketManager;
